/**
 * OPERAÇÕES DE CRÉDITO: AS REGRAS DA ESCRITA (11/10/2026, medição da LoanContractsTab). A tela não paga parcela (as
 * pagas derivam das despesas, no "Sincronizar"); o que ela grava é o cadastro do contrato, e o status vale dinheiro: o
 * card de dívida soma só os contratos `ativo`.
 */

const prestacoesEPagas = (contrato: any, resumo: any) => {
  const carencia = Number(contrato?.gracePeriodMonths) || 0;
  const prestacoes = Number(resumo?.payableInstallments ?? ((Number(contrato?.totalInstallments) || 0) - carencia));
  const pagas = Number(resumo?.paidInstallments ?? contrato?.paidInstallments) || 0;
  return { prestacoes, pagas };
};

/**
 * D1: "Quitado" à mão com prestação em aberto TRAVA. O backend aceita qualquer status, e o card "Dívida total" soma só
 * `ativo`: a dívida sumia do card sem parcela paga, e a despesa recorrente seguia gerando parcelas.
 */
export function porqueNaoQuitar(contrato: any, resumo: any, novoStatus: string): string | null {
  if (novoStatus !== 'quitado') return null;
  if (contrato?.status === 'quitado') return null;
  if (contrato?.type === 'desconto_duplicatas') return null;
  const { prestacoes, pagas } = prestacoesEPagas(contrato, resumo);
  if (prestacoes > 0 && pagas >= prestacoes) return null;
  return `${pagas} de ${Math.max(prestacoes, 0)} prestações constam pagas. Sincronize as parcelas antes; para quitar mesmo assim, o backend precisa aceitar (hoje ele não confere, e a dívida sumiria do card com prestação em aberto).`;
}

/** D4: excluir é soft (o histórico fica), mas a recorrente e as faturas antecipadas seguem como estão. */
export function confirmacaoDeExcluirContrato(c: any, recorrente: { id: number; description?: string; name?: string } | null) {
  const nomeDaRecorrente = recorrente ? (recorrente.description || recorrente.name || `#${recorrente.id}`) : '';
  const partes = ['Sai da lista e dos cards; o histórico fica.'];
  if (recorrente) partes.push(`A despesa recorrente vinculada ("${nomeDaRecorrente}") continua ativa e gerando parcelas: a seguir pergunto se a desativo também.`);
  if (c?.type === 'desconto_duplicatas') partes.push('As faturas marcadas como antecipadas por este borderô continuam marcadas.');
  return {
    title: 'Excluir o contrato?',
    message: `${c?.contractNumber}${c?.description ? ` - ${c.description}` : ''}.`,
    consequence: partes.join(' '),
    danger: true,
    confirmLabel: 'Excluir',
  };
}

export function confirmacaoDeDesativarRecorrente(recorrente: { id: number; description?: string; name?: string }) {
  return {
    title: 'Desativar a despesa recorrente?',
    message: `"${recorrente.description || recorrente.name || `#${recorrente.id}`}" foi a despesa que gerava as parcelas do contrato excluído.`,
    consequence: 'Desativada, ela para de gerar as próximas; as já lançadas ficam.',
    confirmLabel: 'Desativar',
  };
}

/**
 * D3: com a #424 do backend (no ar desde 13/10/2026) o servidor acha a despesa do borderô pela FK (`expenses.loanContractId`),
 * não mais pela descrição, e diz qual foi (`alreadyExisted`, sempre booleano). Renomear a despesa deixou de duplicar as
 * taxas, e o aviso que dizia isso saiu do confirm.
 */
export function confirmacaoDasDespesasDoBordero(c: any) {
  return {
    title: 'Criar as despesas do borderô?',
    message: `${c?.contractNumber}: IOF, tarifa e encargos vão para Despesas.`,
    consequence: 'Se já houver despesa deste borderô, o servidor a reaproveita e não cria outra.',
    confirmLabel: 'Criar',
  };
}

export function avisoDasDespesasDoBordero(r: { created?: boolean; parentId?: number; alreadyExisted?: boolean }): { mensagem: string; tom: 'success' | 'warning' } {
  if (!r?.created || !r.parentId) return { mensagem: 'Nenhuma despesa criada: o borderô não tem taxas, ou o servidor não achou o que lançar.', tom: 'warning' };
  if (r.alreadyExisted === true) return { mensagem: `A despesa #${r.parentId} deste borderô já existia; nada foi criado.`, tom: 'warning' };
  // O braço do "servidor ainda não diz qual" saiu (13/10/2026): desde a #424 o `alreadyExisted` vem sempre.
  return { mensagem: `Despesa #${r.parentId} criada com IOF, tarifa e encargos.`, tom: 'success' };
}

/**
 * A PARCELA VENCIDA (12/10/2026). "Venceu e não consta débito no extrato" AFIRMAVA o que não lia: a próxima parcela sai
 * de `paidInstallments`, que só anda no "Sincronizar parcelas" (não há cron). Medido em produção em 11/10: dois
 * contratos com a despesa do mês PAGA e conciliada e o contador parado; um com a despesa pendente e o extrato da conta
 * só até 06/10. A frase agora é decidida pela DESPESA do mês da parcela e pelo alcance do extrato da conta:
 *  - leitura das despesas falhou → não verificada (nunca afirma);
 *  - despesa paga → "paga em DD/MM; falta sincronizar as parcelas";
 *  - extrato não lido → não verificada;
 *  - despesa pendente (ou ausente) e o extrato sem lançamento depois do vencimento → "o extrato vai até DD/MM";
 *  - pendente com o extrato passando do vencimento → aí sim "venceu e não consta débito".
 */
export type LeituraDaParcela =
  | { estado: 'nao_verificado'; motivo: string }
  | { estado: 'paga'; pagaEm: string | null }
  | { estado: 'extrato_curto'; extratoAte: string | null; temDespesa: boolean }
  | { estado: 'vencida'; temDespesa: boolean };

type DespesaDoContrato = { loanContractId?: number | null; referenceMonth?: string | null; dueDate?: string | null; status?: string; paymentDate?: string | null };

export function despesaDaParcela(despesas: DespesaDoContrato[], contratoId: number, vencimento: string): DespesaDoContrato | undefined {
  const mes = vencimento.slice(0, 7);
  return despesas.find((d) => Number(d.loanContractId) === contratoId && d.status !== 'Cancelado'
    && (d.referenceMonth === mes || String(d.dueDate || '').slice(0, 7) === mes));
}

/** `despesas` null = a leitura falhou; `extratoAte` undefined = não lido, null = a conta não tem lançamento importado. */
export function lerParcelaVencida(contratoId: number, vencimento: string, despesas: DespesaDoContrato[] | null, extratoAte: string | null | undefined): LeituraDaParcela {
  if (despesas === null) return { estado: 'nao_verificado', motivo: 'não consegui ler as despesas do contrato' };
  const d = despesaDaParcela(despesas, contratoId, vencimento);
  if (d?.status === 'Pago') return { estado: 'paga', pagaEm: d.paymentDate ? String(d.paymentDate).slice(0, 10) : null };
  if (extratoAte === undefined) return { estado: 'nao_verificado', motivo: 'não consegui ler o extrato da conta' };
  const ate = extratoAte ? extratoAte.slice(0, 10) : null;
  if (!ate || ate <= vencimento.slice(0, 10)) return { estado: 'extrato_curto', extratoAte: ate, temDespesa: !!d };
  return { estado: 'vencida', temDespesa: !!d };
}

const ddmm = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;

export function fraseDaParcelaVencida(numeroContrato: string, p: { numero: number; total: number; dueDate: string }, l: LeituraDaParcela): string {
  const parcela = `${numeroContrato}: parcela ${p.numero}/${p.total} de ${ddmm(p.dueDate)}`;
  switch (l.estado) {
    case 'paga': return `${parcela}: despesa paga${l.pagaEm ? ` em ${ddmm(l.pagaEm)}` : ''}; falta sincronizar as parcelas`;
    case 'extrato_curto': return `${parcela}: ${l.temDespesa ? 'despesa pendente' : 'sem despesa lançada'}; ${l.extratoAte ? `o extrato desta conta vai até ${ddmm(l.extratoAte)}` : 'esta conta não tem extrato importado'}`;
    case 'vencida': return `${numeroContrato}: parcela ${p.numero}/${p.total} venceu em ${ddmm(p.dueDate)} e não consta débito no extrato`;
    default: return `${parcela}: não verificada (${l.motivo})`;
  }
}

export const ROTULO_DA_PARCELA: Record<LeituraDaParcela['estado'], string> = {
  paga: 'Paga; falta sincronizar',
  extrato_curto: 'Pendente; extrato não cobre',
  vencida: 'Venceu e não consta débito',
  nao_verificado: 'Não verificada',
};
